import { PaletteColor, alpha, PaletteColorOptions, createTheme } from "@mui/material/styles";
import { ChipProps } from "@mui/material/Chip";

declare module "@mui/material/styles" {
  interface Palette {
    textPrimary: PaletteColor;
    textSecondary: PaletteColor;
    textTertiary: PaletteColor;
    textPlaceholder: PaletteColor;
    textDisabled: PaletteColor;
    textLink: PaletteColor;
    fillGray: PaletteColor;
    logoFill: PaletteColor;
    divider2: string;
    white: PaletteColor;
    default: PaletteColor;
    other: {
      outlinedBoder: string;
      filledInputBackground: string;
      borderGray: string;
      tableRowGray: string;
      noResults: string;
      tileShadow: string;
      navigationMain: string; // Thse don't seem to be used. Can they be removed?
      navigationMainContrast: string; // and this
      divider2: string;
      divider3: string;
      backdropOverlay: string;
      fillGray: string;
      logoFill: string;
      snackbar: string;
    };
  }

  interface PaletteBackground {
    paper: string;
    default: string;
    panel1: string;
  }

  interface PaletteOptions {
    textPrimary?: PaletteColorOptions;
    textSecondary?: PaletteColorOptions;
    textTertiary?: PaletteColorOptions;
    textPlaceholder?: PaletteColorOptions;
    textDisabled?: PaletteColorOptions;
    textLink?: PaletteColorOptions;
    fillGray?: PaletteColorOptions;
    logoFill?: PaletteColorOptions;
    divider2?: string;
    white?: PaletteColorOptions;
    default: PaletteColorOptions;
    other: {
      outlinedBoder: string;
      filledInputBackground: string;
      borderGray: string;
      tableRowGray: string;
      noResults: string;
      tileShadow: string;
      navigationMain: string;
      navigationMainContrast: string;
      contrastFill: string;
      divider2: string;
      divider3: string;
      backdropOverlay: string;
      fillGray: string;
      logoFill: string;
      snackbar: string;
    };
  }

  interface TypeBackground {
    paper: string;
    default: string;
    panel1: string;
    panel2: string;
    primary: string;
  }

  interface TypographyVariants {
    body4: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    body4?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    body4: true;
  }
}

type options = {
  mode?: "light" | "dark";
};

export const createFluxTheme = (options: options = {}) => {
  const { mode = "light" } = options;

  const baseTextColor = mode === "light" ? "#2A2A2A" : "#FFFFFF";
  const actionsColor = mode === "light" ? "#636569" : "#FFFFFF";

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 480,
        md: 624,
        lg: 760,
        xl: 996
      }
    },
    palette: {
      mode: mode,
      /* Text colors */
      textPrimary: {
        main: baseTextColor
      },
      textSecondary: {
        main: alpha(baseTextColor, 0.66)
      },
      textTertiary: {
        main: alpha(baseTextColor, 0.56)
      },
      textPlaceholder: {
        main: alpha(baseTextColor, 0.35)
      },
      textDisabled: {
        main: alpha(baseTextColor, 0.45)
      },
      textLink: {
        main: mode === "light" ? "#0081eb" : "#0f9fff"
      },
      /* Theme color palettes */
      default: {
        main: mode === "light" ? "#F0F0F0" : "#3d434d" /* Need this for buttons */,
        dark: "#c7c7c7",
        light: "#F7F7F7",
        contrastText: mode === "light" ? "#414141" : "#ffffff"
      },
      primary: {
        main: "#F96611",
        dark: "#D15005",
        light: "#FF640B",
        contrastText: "#FFFFFF"
      },
      secondary: {
        main: "#222222",
        dark: "#2D2D2F",
        light: "#3B3B3B",
        contrastText: "#FFFFFF"
      },
      success: {
        main: "#11A763",
        dark: "#0B7445",
        light: "#40B882",
        contrastText: "#FFFFFF"
      },
      error: {
        main: "#CA492F",
        dark: "#8D3320",
        light: "#D46D58",
        contrastText: "#FFFFFF"
      },
      warning: {
        main: "#CC8124",
        dark: "#8E5A19",
        light: "#D69A4F",
        contrastText: "#FFFFFF"
      },
      info: {
        main: "#0081EB",
        dark: "#8E5A19",
        light: "#339AEF",
        contrastText: "#FFFFFF"
      },

      /* Other colors */
      divider: mode === "light" ? "#DDE2EA" : "#3a424d",
      white: {
        main: "#FFFFFF",
        light: "#FFFFFF80"
      },
      action: {
        active: actionsColor,
        hover: alpha(actionsColor, 0.08),
        selected: alpha(actionsColor, 0.16),
        disabled: alpha(actionsColor, 0.5),
        disabledBackground: alpha(actionsColor, 0.25),
        focus: alpha(actionsColor, 0.25)
      },
      other: {
        navigationMain: mode === "light" ? "#2e3047" : "#18222f",
        navigationMainContrast: "#ffffff",
        divider2: mode === "light" ? "#E7EBF1" : "#2E363D",
        divider3: mode === "light" ? "#f7f7f7" : "#1f2a38",
        outlinedBoder: mode === "light" ? alpha("#000000", 0.23) : "#556070",
        backdropOverlay: mode === "light" ? alpha("#000000", 0.75) : alpha("#000000", 0.5),
        filledInputBackground: mode === "light" ? alpha("#A8ABB9", 0.05) : alpha("#A8ABB9", 0.02),
        borderGray: "#D9DFE8",
        tableRowGray: "#E7EBF1",
        contrastFill: mode === "light" ? "#F0F9FF" : "#000000",
        fillGray: mode === "light" ? "#F5F5F5" : "#272f3b",
        logoFill: mode === "light" ? "#F8F8F8" : alpha("#FFFFFF", 0.1),
        tileShadow:
          mode === "light"
            ? "0 3px 6px rgba(0,0,0,0.04), inset 0 0 0 1px rgba(0,0,0,0.1)"
            : "0 3px 6px rgba(255,255,255,0.04), inset 0 0 0 1px rgba(255,255,255,0.1)",
        noResults: "#D1D5DB",
        snackbar: "#323232"
      },
      background: {
        primary: "#FFF3EB",
        default: mode === "light" ? "#FFFFFF" : "#121924",
        paper: mode === "light" ? "#FFFFFF" : "#1e2733",
        panel1: mode === "light" ? "#fafafa" : "#0d131c",
        panel2: mode === "light" ? "#f7f7f7" : "#0a0f15"
      }
    },
    components: {
      MuiDialogContent: {
        styleOverrides: {
          root: {
            h6: { fontWeight: 400 }
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 400
          },
          contained: {
            boxShadow: "none",

            "&:hover": {
              backgroundColor: "#0000000"
            }
          },
          outlinedSecondary: {
            borderColor: "#474747",
            "&:hover": {
              backgroundColor: "rgba(100, 100, 100, 0.08)"
            }
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          head: {
            height: "48px"
          }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderColor: "rgb(191, 193, 195)",
            textTransform: "none"
          }
        }
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: "8px"
          }
        }
      },
      MuiChip: {
        variants: [
          {
            props: { variant: "standard" },

            // @ts-expect-error - ownerState is not typed
            //eventually want to be able to type this but since its in an isolated incident, its okay to ignore
            style: ({ theme, ownerState }) => {
              //necessary to access color prop from ChipProps
              const chipState = ownerState as Partial<ChipProps>;
              const color = (chipState.color || "primary") as keyof typeof theme.palette;

              const backgroundColor =
                theme.palette.mode === "light"
                  ? (theme.palette[color] as PaletteColor).light
                  : (theme.palette[color] as PaletteColor).dark;

              // Apply semi-transparent background from design specs
              const bgWithOpacity = `${backgroundColor}14`;

              return {
                backgroundColor: bgWithOpacity,
                color: (theme.palette[color] as PaletteColor).main,
                border: "none",
                "& .MuiChip-deleteIcon": {
                  color: (theme.palette[color] as PaletteColor).main,
                  "&:hover": {
                    color: (theme.palette[color] as PaletteColor).dark
                  }
                },
                "& .MuiChip-icon": {
                  color: (theme.palette[color] as PaletteColor).main
                }
              };
            }
          }
        ]
      }
    },
    typography: {
      fontFamily: ["poppins"].join(","),
      h1: {
        fontSize: "48px",
        lineHeight: "44px",
        fontWeight: 400,
        letterSpacing: -1,
        fontFamily: ["Instrument Serif"].join(",")
      },
      h2: {
        fontSize: "36px",
        lineHeight: "44px",
        fontWeight: 500
      },
      h3: {
        fontSize: "32px",
        lineHeight: "40px",
        fontWeight: 500
      },
      h4: {
        fontSize: "24px",
        lineHeight: "32px",
        fontWeight: 500
      },
      h5: {
        fontSize: "20px",
        lineHeight: "28px",
        fontWeight: 500
      },
      h6: {
        fontSize: "16px",
        lineHeight: "24px",
        fontWeight: 500
      },
      subtitle1: {
        fontSize: "14px",
        lineHeight: "20px",
        fontWeight: 500
      },
      subtitle2: {
        fontSize: "12px",
        lineHeight: "16px",
        fontWeight: 500
      },
      body1: {
        fontSize: "16px",
        lineHeight: "24px",
        fontWeight: 400
      },
      body2: {
        fontSize: "14px",
        lineHeight: "20px",
        fontWeight: 400
      },
      body3: {
        fontSize: "12px",
        lineHeight: "16px",
        fontWeight: 400
      },
      body4: {
        fontSize: "10px",
        lineHeight: "16px",
        fontWeight: 400
      },
      caption: {
        fontSize: "12px",
        lineHeight: "16px",
        fontWeight: 400
      },
      overline: {
        fontSize: "12px",
        lineHeight: "16px",
        fontWeight: 500
      }
    }
  });
};

export const fluxTheme = createFluxTheme();
